/**
 * Scoped file links (API S-55 follow-up, 2026-10-28): every `DownloadUrl` / `DisplayUrl` of a file that is not a public
 * asset is a link for the signed-in user that stops opening after 5 minutes (`media/publicDownload|publicDisplay?id&uid
 * &ws&exp&hash`), and as soon as they lose access. Pages and the query cache keep API answers longer than that, so the
 * app refreshes them where they are used, once for every module:
 * - a click on such a link (left, Ctrl/Cmd, middle) always asks `GET media/link?id=` for a fresh one and opens that
 *   instead, in the same tab or a new one as the link would have — whatever its `exp` says, so a device clock that is
 *   off never lets a dead link through;
 * - an image whose scoped `src` failed to load gets a fresh inline link (once per failed address).
 * The fresh address must itself be a scoped link of the API before it is opened. Permanent links (logos, avatars: no
 * `exp`) and links to other hosts are left alone. A refusal (no access any more) says so in a toast. Pure helpers
 * unit-tested in tests/unit/app/media-links.spec.ts.
 */
import { http } from '@/api/http'
import { toast } from '@/shared/composables/useToast'

import { getConfig } from './config'
import { getI18n } from './i18n'

export interface MediaLink {
  /** The Media id (`GET media/link?id=`). */
  id: number
  /** `publicDisplay` (shown in the browser) rather than `publicDownload`. */
  inline: boolean
  /** Expiry, Unix seconds. */
  exp: number
}

/** A scoped link of the API, else `null` (another host, another path, a permanent link without `exp`). */
export function parseMediaLink(href: string | null | undefined, apiUrl: string, base?: string): MediaLink | null {
  if (!href) return null
  let url: URL
  let api: URL
  try {
    url = new URL(href, base)
    api = new URL(apiUrl, base)
  } catch {
    return null
  }
  if (url.origin !== api.origin || !url.pathname.startsWith(api.pathname)) return null
  const action = url.pathname.slice(api.pathname.length).replace(/^\/+/, '')
  if (action !== 'media/publicDownload' && action !== 'media/publicDisplay') return null
  const id = Number(url.searchParams.get('id'))
  const exp = Number(url.searchParams.get('exp'))
  if (!Number.isInteger(id) || id <= 0 || !url.searchParams.has('exp') || !Number.isFinite(exp)) return null
  const inline = action === 'media/publicDisplay' ? !url.searchParams.has('download') : url.searchParams.has('inline')
  return { id, inline, exp }
}

interface FreshLink {
  url: string
  expiresAt: string
}

/**
 * `GET media/link` — a new 5-minute link after a fresh access check; the API's `url`, made absolute. Refused unless it
 * is a scoped file link of the API itself (never another site or a `javascript:` address from a tampered answer).
 */
export async function freshMediaUrl(link: Pick<MediaLink, 'id' | 'inline'>): Promise<string> {
  const { data } = await http.get<FreshLink>('media/link', {
    params: link.inline ? { id: link.id, inline: 1 } : { id: link.id },
  })
  const apiUrl = getConfig().apiUrl
  const url = new URL(data.url, new URL(apiUrl, window.location.href)).toString()
  if (parseMediaLink(url, apiUrl, window.location.href)?.id !== link.id) throw new Error('Unexpected file link')
  return url
}

function refused(): void {
  toast.danger(getI18n().global.t('common.file.linkUnavailable'))
}

async function open(link: MediaLink, newTab: boolean): Promise<void> {
  // A tab opened now, inside the click, is not a blocked popup; it gets the address once the API answers.
  const tab = newTab ? window.open('', '_blank') : null
  if (tab) tab.opener = null
  try {
    const url = await freshMediaUrl(link)
    if (tab) tab.location.href = url
    else if (newTab) window.open(url, '_blank', 'noopener')
    else window.location.assign(url)
  } catch {
    tab?.close()
    refused()
  }
}

function linkOf(event: MouseEvent): { anchor: HTMLAnchorElement; link: MediaLink } | null {
  const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
  if (!anchor) return null
  const link = parseMediaLink(anchor.getAttribute('href'), getConfig().apiUrl, window.location.href)
  return link ? { anchor, link } : null
}

/** Installs the click and image handlers on `target`; returns their removal (tests). */
export function installMediaLinks(target: Document = document): () => void {
  function onClick(event: MouseEvent): void {
    if (event.button !== 0 && event.button !== 1) return
    const found = linkOf(event)
    if (!found) return
    // A link the page previews on a plain click (`data-file-preview`, AppFilePreview): the page's own handler opens
    // the previewer; Ctrl / Cmd / Shift / middle click still open the file in a new tab here.
    const plain = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey
    if (plain && found.anchor.hasAttribute('data-file-preview')) return
    event.preventDefault()
    const newTab =
      event.button === 1 || event.ctrlKey || event.metaKey || event.shiftKey || found.anchor.target === '_blank'
    void open(found.link, newTab)
  }

  function onImageError(event: Event): void {
    const image = event.target
    if (!(image instanceof HTMLImageElement)) return
    const src = image.getAttribute('src')
    // The address this handler set failed too: stop. A new `src` (Vue reusing the element, a later expiry) is retried.
    if (!src || src === image.dataset.mediaRefreshed) return
    const link = parseMediaLink(src, getConfig().apiUrl, window.location.href)
    if (!link) return
    freshMediaUrl({ id: link.id, inline: true })
      .then((url) => {
        image.dataset.mediaRefreshed = url
        image.src = url
      })
      .catch(() => undefined)
  }

  target.addEventListener('click', onClick, true)
  target.addEventListener('auxclick', onClick, true)
  // `error` does not bubble: caught on the way down.
  target.addEventListener('error', onImageError, true)
  return () => {
    target.removeEventListener('click', onClick, true)
    target.removeEventListener('auxclick', onClick, true)
    target.removeEventListener('error', onImageError, true)
  }
}
